/**
 * ForwardTab — Deposit / Forward setup theo SOP.
 * Fields: margin_value_per (Deposit Value %), margin_limit_per (Limit %),
 * time_limit (days), margin_bonus_per (Bonus %), margin_interest_month
 * (Interest %/month), deposit_repayment_date.
 */
import { Box, TextField } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { Controller, type Control, type FieldErrors } from 'react-hook-form'

import type { LocalPFormValues } from './types'

export interface ForwardTabProps {
  control: Control<LocalPFormValues>
  errors: FieldErrors<LocalPFormValues>
}

export function ForwardTab({ control, errors }: ForwardTabProps) {
  return (
    <Box display="flex" flexDirection="column" gap={2} pt={1}>
      <Box display="flex" gap={2} flexWrap="wrap">
        <Controller
          control={control}
          name="margin_value_per"
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value === '' ? '' : field.value}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : Number(e.target.value))
              }
              label="Deposit Value %"
              type="number"
              size="small"
              sx={{ flex: '1 1 180px' }}
              error={!!errors.margin_value_per}
              helperText={errors.margin_value_per?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="margin_limit_per"
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value === '' ? '' : field.value}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : Number(e.target.value))
              }
              label="Deposit Limit %"
              type="number"
              size="small"
              sx={{ flex: '1 1 180px' }}
              error={!!errors.margin_limit_per}
              helperText={errors.margin_limit_per?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="time_limit"
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value === '' ? '' : field.value}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : Number(e.target.value))
              }
              label="Time Limit (days)"
              type="number"
              size="small"
              sx={{ flex: '1 1 180px' }}
              error={!!errors.time_limit}
              helperText={errors.time_limit?.message}
            />
          )}
        />
      </Box>

      <Box display="flex" gap={2} flexWrap="wrap">
        <Controller
          control={control}
          name="margin_bonus_per"
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value === '' ? '' : field.value}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : Number(e.target.value))
              }
              label="Deposit Bonus %"
              type="number"
              size="small"
              sx={{ flex: '1 1 180px' }}
              error={!!errors.margin_bonus_per}
              helperText={errors.margin_bonus_per?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="margin_interest_month"
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value === '' ? '' : field.value}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : Number(e.target.value))
              }
              label="Interest %/month"
              type="number"
              size="small"
              sx={{ flex: '1 1 180px' }}
              error={!!errors.margin_interest_month}
              helperText={errors.margin_interest_month?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="deposit_repayment_date"
          render={({ field }) => (
            <DatePicker
              label="Deposit Repayment Date"
              format="DD/MM/YYYY"
              value={field.value ? dayjs(field.value) : null}
              onChange={(d) => field.onChange(d ? d.format('YYYY-MM-DD') : '')}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { flex: '1 1 220px' },
                  error: !!errors.deposit_repayment_date,
                  helperText: errors.deposit_repayment_date?.message,
                },
              }}
            />
          )}
        />
      </Box>
    </Box>
  )
}

export default ForwardTab
